/**
 * Rax Skill Capability Card Component
 *
 * Shows what a skill can do, what it accesses, and example scenarios.
 * Key to transparency: users understand exactly what they're enabling.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface SkillCapability {
  id: string;
  name: string;
  description: string;
  icon: string;
  enabled: boolean;

  /** What permissions/data the skill accesses */
  accesses: SkillAccess[];

  /** The data flow: how input becomes output */
  valueChain: string[];

  /** Example prompts/scenarios */
  scenarios: string[];

  /** Data handling rules */
  dataRules: SkillDataRule[];

  /** OAuth required? */
  requiresAuth?: boolean;
  authStatus?: "connected" | "disconnected" | "expired";
}

export interface SkillAccess {
  scope: string;
  description: string;
  level: "read" | "write" | "admin";
}

export interface SkillDataRule {
  type: "retention" | "exclusion" | "encryption";
  icon: string;
  text: string;
}

@customElement("rax-skill-card")
export class RaxSkillCard extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .skill-card {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
        transition: all var(--transition-fast);
      }
      
      .skill-card:hover {
        border-color: var(--text-tertiary);
        box-shadow: var(--shadow-md);
      }
      
      .skill-card.expanded {
        border-color: var(--rax-yellow);
        box-shadow: var(--shadow-lg);
      }
      
      /* Header */
      .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4) var(--space-5);
        cursor: pointer;
      }
      
      .skill-info {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }
      
      .skill-icon {
        font-size: 28px;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-primary);
        border-radius: var(--radius-md);
      }
      
      .skill-text {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .skill-name {
        font-weight: 600;
        font-size: var(--font-md);
        color: var(--text-primary);
      }
      
      .skill-description {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .skill-actions {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }
      
      .auth-badge {
        font-size: var(--font-xs);
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-sm);
        font-weight: 500;
      }
      
      .auth-badge.connected {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }
      
      .auth-badge.disconnected {
        background: rgba(245, 158, 11, 0.2);
        color: var(--warning);
      }
      
      .auth-badge.expired {
        background: rgba(239, 68, 68, 0.2);
        color: var(--error);
      }
      
      /* Toggle Switch */
      .toggle-switch {
        position: relative;
        width: 48px;
        height: 26px;
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: 13px;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .toggle-switch.enabled {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
      }
      
      .toggle-knob {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        background: var(--text-primary);
        border-radius: 50%;
        transition: transform var(--transition-fast);
        box-shadow: var(--shadow-sm);
      }
      
      .toggle-switch.enabled .toggle-knob {
        transform: translateX(22px);
        background: var(--rax-black);
      }
      
      /* Expanded Content */
      .card-content {
        border-top: 1px solid var(--border-color);
        padding: 0;
        max-height: 0;
        overflow: hidden;
        transition:
          max-height var(--transition-slow),
          padding var(--transition-slow);
      }
      
      .skill-card.expanded .card-content {
        max-height: 600px;
        padding: var(--space-5);
      }
      
      /* Sections */
      .section {
        margin-bottom: var(--space-5);
      }
      
      .section:last-child {
        margin-bottom: 0;
      }
      
      .section-title {
        font-size: var(--font-xs);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--text-tertiary);
        margin-bottom: var(--space-2);
      }
      
      /* Accesses */
      .access-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .access-item {
        display: flex;
        align-items: flex-start;
        gap: var(--space-2);
        font-size: var(--font-xs);
      }
      
      .access-bullet {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        margin-top: 6px;
        flex-shrink: 0;
      }
      
      .access-bullet.read {
        background: var(--success);
      }
      
      .access-bullet.write {
        background: var(--warning);
      }
      
      .access-bullet.admin {
        background: var(--error);
      }
      
      .access-text {
        flex: 1;
        color: var(--text-secondary);
      }
      
      .access-scope {
        font-weight: 500;
        color: var(--text-primary);
      }
      
      /* Value Chain */
      .value-chain {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
        font-size: var(--font-xs);
      }
      
      .chain-step {
        padding: var(--space-1) var(--space-3);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-full);
        color: var(--text-secondary);
      }
      
      .chain-arrow {
        color: var(--rax-yellow);
        font-weight: bold;
      }
      
      /* Scenarios */
      .scenario-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .scenario-item {
        font-size: var(--font-xs);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-subtle);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-style: italic;
        color: var(--text-secondary);
      }
      
      .scenario-item::before {
        content: '"';
        color: var(--rax-yellow);
      }
      
      .scenario-item::after {
        content: '"';
        color: var(--rax-yellow);
      }
      
      /* Data Rules */
      .rules-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .rule-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .rule-icon {
        font-size: 14px;
      }
      
      /* Connect Button */
      .connect-btn {
        width: 100%;
        padding: var(--space-3);
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .connect-btn.connect {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .connect-btn.connect:hover {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      .connect-btn.disconnect {
        background: rgba(239, 68, 68, 0.1);
        color: var(--error);
        border: 1px solid rgba(239, 68, 68, 0.3);
      }
      
      .connect-btn.disconnect:hover {
        background: rgba(239, 68, 68, 0.2);
      }
    `,
  ];

  @property({ type: Object })
  skill!: SkillCapability;

  @state()
  private expanded = false;

  override render() {
    const { skill } = this;
    if (!skill)
      return html`
        
      `;

    return html`
      <div class="skill-card ${this.expanded ? "expanded" : ""}">
        <div class="card-header" @click=${this.toggleExpanded}>
          <div class="skill-info">
            <div class="skill-icon">${skill.icon}</div>
            <div class="skill-text">
              <div class="skill-name">${skill.name}</div>
              <div class="skill-description">${skill.description}</div>
            </div>
          </div>
          <div class="skill-actions">
            ${
              skill.requiresAuth && skill.authStatus
                ? html`
              <span class="auth-badge ${skill.authStatus}">
                ${this.getAuthStatusText(skill.authStatus)}
              </span>
            `
                : ""
            }
            <div
              class="toggle-switch ${skill.enabled ? "enabled" : ""}"
              @click=${this.onToggle}
            >
              <div class="toggle-knob"></div>
            </div>
          </div>
        </div>

        <div class="card-content">
          <!-- Accesses -->
          <div class="section">
            <div class="section-title">Accesses</div>
            <div class="access-list">
              ${skill.accesses.map(
                (access) => html`
                <div class="access-item">
                  <div class="access-bullet ${access.level}"></div>
                  <div class="access-text">
                    <span class="access-scope">${access.scope}</span>
                    — ${access.description}
                  </div>
                </div>
              `,
              )}
            </div>
          </div>

          <!-- Value Chain -->
          <div class="section">
            <div class="section-title">Value Chain</div>
            <div class="value-chain">
              ${skill.valueChain.map(
                (step, i) => html`
                ${
                  i > 0
                    ? html`
                        <span class="chain-arrow">→</span>
                      `
                    : ""
                }
                <span class="chain-step">${step}</span>
              `,
              )}
            </div>
          </div>

          <!-- Scenarios -->
          <div class="section">
            <div class="section-title">Scenarios</div>
            <div class="scenario-list">
              ${skill.scenarios.map(
                (scenario) => html`
                <div class="scenario-item">${scenario}</div>
              `,
              )}
            </div>
          </div>

          <!-- Data Rules -->
          <div class="section">
            <div class="section-title">Data Rules</div>
            <div class="rules-list">
              ${skill.dataRules.map(
                (rule) => html`
                <div class="rule-item">
                  <span class="rule-icon">${rule.icon}</span>
                  <span>${rule.text}</span>
                </div>
              `,
              )}
            </div>
          </div>

          <!-- Connect/Disconnect Button -->
          ${
            skill.requiresAuth
              ? html`
            <div class="section">
              ${
                skill.authStatus === "connected"
                  ? html`
                <button class="connect-btn disconnect" @click=${this.onDisconnect}>
                  Disconnect ${skill.name}
                </button>
              `
                  : html`
                <button class="connect-btn connect" @click=${this.onConnect}>
                  Connect ${skill.name}
                </button>
              `
              }
            </div>
          `
              : ""
          }
        </div>
      </div>
    `;
  }

  private toggleExpanded(e: Event) {
    // Don't toggle if clicking the toggle switch
    if ((e.target as HTMLElement).closest(".toggle-switch")) return;
    this.expanded = !this.expanded;
  }

  private onToggle(e: Event) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("skill-toggle", {
        bubbles: true,
        detail: { skillId: this.skill.id, enabled: !this.skill.enabled },
      }),
    );
  }

  private onConnect() {
    this.dispatchEvent(
      new CustomEvent("skill-connect", {
        bubbles: true,
        detail: { skillId: this.skill.id },
      }),
    );
  }

  private onDisconnect() {
    this.dispatchEvent(
      new CustomEvent("skill-disconnect", {
        bubbles: true,
        detail: { skillId: this.skill.id },
      }),
    );
  }

  private getAuthStatusText(status: string): string {
    switch (status) {
      case "connected":
        return "Connected";
      case "disconnected":
        return "Not connected";
      case "expired":
        return "Reconnect needed";
      default:
        return status;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-skill-card": RaxSkillCard;
  }
}
